import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { useToast } from "@/context/ToastContext";
import { userMigrationApi } from "@/lib/api";

interface RoleMigrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onMigrationSuccess?: () => void;
}

interface RoleMigrationStats {
    total_roles_v1: number;
    roles_created: number;
    roles_skipped: number;
    errors: string[];
}

export const RoleMigrationModal: React.FC<RoleMigrationModalProps> = ({
    isOpen,
    onClose,
    onMigrationSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [testing, setTesting] = useState<boolean>(false);
    const [migrating, setMigrating] = useState<boolean>(false);
    const [connectionResult, setConnectionResult] = useState<{
        success: boolean;
        message: string;
        roles_count?: number;
    } | null>(null);
    const [stats, setStats] = useState<RoleMigrationStats | null>(null);
    const statsEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (stats && statsEndRef.current) {
            statsEndRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
            });
        }
    }, [stats]);

    const handleTestConnection = async (): Promise<void> => {
        setTesting(true);
        setConnectionResult(null);

        try {
            const res = await userMigrationApi.testRoles();
            const connData =
                (
                    res as {
                        data?: {
                            success: boolean;
                            message: string;
                            roles_count?: number;
                        };
                    }
                )?.data || res;
            if (connData) {
                setConnectionResult(connData);
                if (connData.success) {
                    showSuccess(t("tools.role_migration.connection_ok"));
                } else {
                    showError(
                        connData.message ||
                            t("tools.role_migration.connection_error")
                    );
                }
            }
        } catch (error: unknown) {
            console.error(error);
            const err = error as {
                response?: {
                    data?: { message?: string; data?: { message?: string } };
                };
                message?: string;
            };
            const errorMsg =
                err?.response?.data?.message ||
                err?.response?.data?.data?.message ||
                err?.message ||
                t("tools.role_migration.connection_error");
            showError(errorMsg);
            setConnectionResult({
                success: false,
                message: errorMsg,
            });
        } finally {
            setTesting(false);
        }
    };

    const handleRunMigration = async (): Promise<void> => {
        setMigrating(true);
        setStats(null);

        try {
            const res = await userMigrationApi.runRoles();
            if (res?.data) {
                setStats(res.data);
                showSuccess(t("tools.role_migration.migration_success"));
                onMigrationSuccess?.();
            }
        } catch (error: unknown) {
            console.error(error);
            const err = error as {
                response?: {
                    data?: {
                        message?: string;
                        errors?: Record<string, unknown>;
                    };
                };
                message?: string;
            };
            const errorMsg =
                err?.response?.data?.message ||
                (err?.response?.data?.errors
                    ? Object.values(err.response.data.errors).flat().join(" ")
                    : "") ||
                err?.message ||
                t("tools.role_migration.migration_error");

            showError(errorMsg);
            setStats({
                roles_created: 0,
                roles_skipped: 0,
                total_roles: 0,
                tenants_processed: 0,
                errors: [errorMsg],
            });
        } finally {
            setMigrating(false);
        }
    };

    const handleClose = (): void => {
        if (!migrating) {
            setConnectionResult(null);
            setStats(null);
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t("tools.role_migration.title")}
            maxWidth="xl"
            cancelLabel={t("tools.role_migration.close")}
            onSubmit={handleRunMigration}
            submitLabel={t("tools.role_migration.run_migration")}
            submittingLabel={t("tools.role_migration.migrating")}
            isSubmitting={migrating}
            submitDisabled={migrating || testing}
        >
            <div className="flex flex-col items-center justify-center p-2 space-y-6">
                <div className="flex flex-col items-center justify-center space-y-3 text-center">
                    <Icon
                        value="shield"
                        size={32}
                        color="primary"
                        background
                        className="mb-2"
                    />
                    <p className="text-sm text-slate-600 dark:text-zinc-300 text-center">
                        {t("tools.role_migration.modal_subtitle")}
                    </p>
                </div>

                {/* Scope notice: migrating all companies */}
                <div className="w-full rounded-xl border p-4 text-center flex flex-col items-center justify-center gap-1 border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20">
                    <Icon value="globe" size={18} className="text-amber-600" />
                    <p className="text-xs font-semibold mt-1 text-amber-900 dark:text-amber-200">
                        {t("tools.role_migration.scope_all_title")}
                    </p>
                    <p className="text-xs leading-relaxed text-amber-700 dark:text-amber-300">
                        {t("tools.role_migration.scope_all_desc")}
                    </p>
                </div>

                {/* Information Card */}
                <div className="w-full rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900/50 dark:bg-blue-950/20 text-center flex flex-col items-center justify-center">
                    <Icon
                        value="checkCircle"
                        size={20}
                        className="text-blue-600 mb-1"
                    />
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 text-center">
                        {t("tools.role_migration.check_notice_title")}
                    </h4>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 leading-relaxed text-center">
                        {t("tools.role_migration.check_notice_desc")}
                    </p>
                </div>

                {/* Connection Box Centered */}
                <div className="w-full flex flex-col items-center justify-center text-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-card">
                    <div>
                        <p className="text-sm font-semibold text-slate-800 dark:text-zinc-200">
                            {t("tools.role_migration.test_connection")}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {t("tools.tender_migration.ssh_config_hint")}
                        </p>
                    </div>

                    <Button
                        variant="secondary"
                        onClick={handleTestConnection}
                        disabled={testing || migrating}
                    >
                        <div className="flex items-center gap-2">
                            <Icon
                                value={testing ? "refresh" : "check"}
                                size={15}
                                className={testing ? "animate-spin" : ""}
                            />
                            <span>
                                {testing
                                    ? t(
                                          "tools.role_migration.testing_connection"
                                      )
                                    : t("tools.role_migration.test_connection")}
                            </span>
                        </div>
                    </Button>

                    {connectionResult && (
                        <div
                            className={`w-full p-4 rounded-xl border text-sm flex flex-col items-center justify-center text-center gap-2 ${
                                connectionResult.success
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800/40 dark:text-emerald-300"
                                    : "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/20 dark:border-rose-800/40 dark:text-rose-300"
                            }`}
                        >
                            <Icon
                                value={
                                    connectionResult.success
                                        ? "checkCircle"
                                        : "alertCircle"
                                }
                                size={22}
                                className="shrink-0"
                            />
                            <div className="w-full">
                                <p className="font-semibold text-center break-words">
                                    {connectionResult.message}
                                </p>
                                {connectionResult.roles_count !== undefined && (
                                    <p className="text-xs mt-1 text-center font-medium">
                                        {t("tools.role_migration.roles_found", {
                                            count: connectionResult.roles_count,
                                        })}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Migration Stats */}
                {stats && (
                    <div className="w-full space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-card text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                            <Icon
                                value="checkCircle"
                                size={16}
                                className="text-emerald-600"
                            />
                            <span className="font-semibold text-slate-900 dark:text-white">
                                {t("tools.role_migration.results_title")}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.role_migration.total_v1_roles")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-slate-800 dark:text-zinc-200">
                                    {stats.total_roles_v1}
                                </p>
                            </div>

                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.role_migration.roles_created")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-emerald-600">
                                    {stats.roles_created}
                                </p>
                            </div>

                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tools.role_migration.roles_skipped")}
                                </p>
                                <p className="text-lg font-bold mt-1 text-amber-600">
                                    {stats.roles_skipped}
                                </p>
                            </div>
                        </div>

                        {stats.errors?.length > 0 && (
                            <div className="mt-2 text-xs text-rose-600 space-y-1 text-center">
                                {stats.errors.map((err: string, i: number) => (
                                    <p key={i}>• {err}</p>
                                ))}
                            </div>
                        )}

                        <div ref={statsEndRef} />
                    </div>
                )}
            </div>
        </Modal>
    );
};
